export function getPlural(key) {
  const overwriteMap = {
    state: 'state',
    dependency: 'dependencies',
    font_family: 'font_families',
    typography: 'typographies'
  }
  return overwriteMap[key] || key.endsWith('s') ? key : `${key}s`
}

export function getSingular(key) {
  const overwriteMap = {
    dependencies: 'dependency'
  }
  return overwriteMap[key] || (key.endsWith('s') && key.slice(0, -1)) || key
}

export function getType(target) {
  const hasType = target && target.type
  if (hasType) {
    return this.getPlural(target.type)
  }
  const itemKey = this.call('isString', target) ? target : target?.key || this.state.key
  const schema = this.getSchema()
  return (
    this.getTypeByKey(itemKey) || Object.keys(schema).find((key) => itemKey in schema[key]) || null
  )
}

export function getTypeByKey(val) {
  const key = val || this.state?.key
  if (!key) {
    return
  }
  const isPage = key.startsWith('/')
  if (isPage) {
    return 'pages'
  }
  const isComponent = /^[A-Z]*$/u.test(key.slice(0, 1))
  if (isComponent) {
    return 'components'
  }
  const isFile = /^[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*\.[a-zA-Z0-9]+$/u.test(key)
  if (isFile) {
    return 'files'
  }
  const isDependecy = /^(?:@[a-z-~][a-z-._~]*\/)?[a-z-~][a-z-._~]*$/u.test(key)
  if (isDependecy) {
    return 'dependencies'
  }
  const isFunction = /^[a-z][a-zA-Z0-9_]*$/u.test(key)
  if (isFunction) {
    return 'functions'
  }
}

export function getSchemaItem(key, type) {
  const schema = this.getSchema()
  if (!type) {
    type = this.getType(key)
  }
  type = this.getPlural(type)
  if (key && type && schema[type]?.[key]) {
    return schema[type][key]
  }
  const parentObject = Object.values(schema).find((childObj) => key in childObj)
  return parentObject ? parentObject[key] : null
}

export function getDataItem(key, type) {
  if (!key) {
    return null
  }
  if (!type) {
    type = this.getType(key)
  }

  type = this.getPlural(type)

  if (type && this.getData(type)?.[key]) {
    return this.getData(type)[key]
  }

  const parentObject = Object.values(this.getData()).find((childObj) => key in childObj)

  return parentObject ? parentObject[key] : null
}

export function getItem(key, type) {
  if (!this.getDataItem(key, type) && !this.getSchemaItem(key, type)) {
    return
  }
  return {
    ...this.getSchemaItem(key, type),
    value: this.getDataItem(key, type)
  }
}

export function prepareSchemaItem(obj, forcedKey, forcedType) {
  let { value, ...schema } = this.getParsedState(obj)
  // if item has a new name
  if (forcedKey && forcedKey !== schema.key) {
    schema = this.call('deepClone', schema)
    schema.key = forcedKey
  }
  if (forcedType && forcedType !== schema.type) {
    schema.type = forcedType
  }
  return schema
}

export function setItemSchema() {}

export function prepareDataItem(obj) {
  const { value } = this.getParsedState(obj)
  return value
}

export function setItemData() {}

export function validateFormat(
  val,
  arr = [
    'png',
    'svg',
    'jpeg',
    'jpg',
    'gif',
    'bmp',
    'tiff',
    'tif',
    'webp',
    'ico', // Image
    'mp4',
    'webm',
    'avi',
    'mov',
    'wmv',
    'flv',
    'mkv',
    '3gp', // Video
    'mp3',
    'wav',
    'ogg',
    'm4a',
    'flac',
    'aac',
    'wma',
    'mid',
    'midi', // Audio
    'pdf',
    'doc',
    'docx',
    'ppt',
    'pptx',
    'xls',
    'xlsx', // Documents
    'zip',
    'rar',
    '7z', // Archive
    'woff',
    'woff2',
    'ttf',
    'otf' // Fonts
  ]
) {
  return arr.includes(val)
}

export function validateItem(obj) {
  const type = this.getType(obj)
  const { key, value } = obj
  if (!type) {
    return console.error('Type of item is uknown', obj)
  }
  if (!key) {
    return console.error(`${type} does not have a key`, obj)
  }
  if (!value) {
    return console.error(`"${key}" ${type} does not have a value`, obj)
  }

  // Migrated from `window.alert(...)` → `el.call('alertDialog', { ... })`.
  // alertDialog is fire-and-forget (no value returned, the original code
  // didn't read the return value either — `return` was used purely to bail
  // the validation function). The unified dialog matches the app's design
  // system instead of the host OS chrome.
  if (type === 'component') {
    if (!/^[A-Z]*$/u.test(key.slice(0, 1))) {
      this.call('alertDialog', {
        title: this.call('polyglot', 'editor.db.invalidComponentKey'),
        message: this.call('polyglot', 'editor.db.componentKeyCapital').replace('{key}', key)
      })
      return
    }
    if (!/^[a-zA-Z0-9._]+$/u.test(key.slice(0, 1))) {
      this.call('alertDialog', {
        title: this.call('polyglot', 'editor.db.invalidComponentKey'),
        message: this.call('polyglot', 'editor.db.componentKeyChars').replace('{key}', key)
      })
      return
    }
  } else if (type === 'page') {
    if (!key.startsWith('/')) {
      this.call('alertDialog', {
        title: this.call('polyglot', 'editor.db.invalidPageKey'),
        message: this.call('polyglot', 'editor.db.pageKeySlash').replace('{key}', key)
      })
      return
    }
  } else if (type === 'function') {
    if (!this.call('isFunction', value)) {
      this.call('alertDialog', {
        title: this.call('polyglot', 'editor.db.invalidFunction'),
        message: this.call('polyglot', 'editor.db.notAFunction').replace('{key}', key)
      })
      return
    }
  } else if (type === 'file') {
    if (obj.contentType === 'file') {
      if (!obj.format) {
        this.call('alertDialog', {
          title: this.call('polyglot', 'editor.db.invalidFile'),
          message: this.call('polyglot', 'editor.db.fileFormatInvalid').replace('{key}', key)
        })
        return
      }
      if (!this.validateFormat(obj.format)) {
        this.call('alertDialog', {
          title: this.call('polyglot', 'editor.db.unsupportedFormat'),
          message: this.call('polyglot', 'editor.db.formatNotSupported').replace(
            '{format}',
            obj.format
          )
        })
        return
      }
    }
    if (obj.contentType === 'file' && !value) {
      this.call('alertDialog', {
        title: this.call('polyglot', 'editor.db.invalidFile'),
        message: this.call('polyglot', 'editor.db.fileSourceInvalid').replace('{key}', key)
      })
      return
    }
  }

  return true
}

// export async function updateItem(obj, forcedKey, opts = {}) {
//   if (!this.validateItem(obj)) {
//     return
//   }
//   const platformState = this.getRootState()
//   const schemaItem = this.setItemSchema(obj, forcedKey)
//   const dataItem = this.setItemData(obj, forcedKey)
//   const type = this.getType(obj)
//   const { key } = obj

//   const commit = forcedKey
//     ? `Duplicated "${key}" as ${forcedKey} into ${type}.`
//     : `Added ${key} into ${type}`
//   await platformState.update(
//     {
//       ...schemaItem,
//       ...dataItem
//     },
//     {
//       commit,
//       preventUpdate: true,
//       syncOnly: true,
//       saveOnly: true,
//       ...opts
//     }
//   )
//   return obj
// }

// export async function setItem(obj, ...args) {
//   const type = this.getType(obj)
//   const { key } = obj
//   const found = this.getSchemaItem(key, type)
//   if (found) {
//     return window.alert(`"${key}" in ${type} already exists`)
//   }
//   return await this.updateItem(obj, ...args)
// }

export function findNewName(type, key, opts = {}) {
  let index = 0
  const isPage = type === 'pages' || key.startsWith('/')

  while (true) {
    let itemExists

    if (type === 'global_theme') {
      itemExists = this.getDesignSystem('theme').document[key]
    } else if (type === 'typography' && key.charAt(0) !== '@') {
      itemExists = this.getDesignSystem('typography').templates[key]
    } else if (this.call('hasType', 'DESIGN_SYSTEM_TYPES', type)) {
      itemExists = this.getDesignSystem(type)[key]
    } else {
      itemExists = this.getSchemaItem(key, type) || this.getDataItem(key, type)
    }

    if (!itemExists) {
      break
    }

    const suffix = (isPage ? '-copy' : 'Copy') + (index || '')
    const msg = 'Component with such key exists. Please type the new key'
    index++
    if (opts.autoRename) {
      key = key + suffix
      continue
    }
    // TODO(@symbo.ls/windows migration): convert this synchronous prompt
    // loop to a Promise-wrapped promptDialog. Skipping for now because
    // the surrounding function (findUniqueKey) returns a value its
    // callers consume synchronously — fully migrating means making this
    // function async and updating every consumer call site. Tracked as
    // follow-up; the validation alerts in the same file (above) have
    // already been migrated to the unified dialog.
    key = window.prompt(msg, key + suffix)
    if (!key) {
      return
    }
  }

  return key
}

export async function convertToDomql3() {
  const data = this.getData()
  const update = []

  ;['pages', 'components'].forEach((type) => {
    Object.keys(data[type]).forEach((itemKey) => {
      const value = data[type][itemKey]
      if (!value) return
      else {
        update.push(['update', [type, itemKey], value])
        update.push(['delete', ['schema', type, itemKey, 'code']])
        update.push(['delete', ['schema', type, itemKey, 'codeStr']])
      }
    })
  })

  if (update.length) {
    this.sdk.updateData(update, {
      message: `Converted ${update.length} to domql3 format`
    })

    this.call('openNotification', {
      title: this.call('polyglot', 'editor.db.convertedDomql3.title'),
      message: this.call('polyglot', 'editor.db.convertedDomql3.message').replace(
        '{count}',
        String(update.length)
      ),
      type: 'success'
    })
  }
}

export async function restoreMissingTypes() {
  const data = this.getData()
  const schema = this.getSchema()
  const update = []

  ;['pages', 'components', 'functions', 'files', 'state'].forEach((type) => {
    Object.keys(data[type]).forEach((itemKey) => {
      const schemaItem = schema[type][itemKey]
      if (schemaItem && schemaItem.type && schemaItem.type.endsWith('s')) return
      else {
        update.push(['update', ['schema', type, itemKey, 'type'], type])
      }
    })
  })

  if (update.length) {
    this.sdk.updateData(update, {
      message: `Restored ${update.length} types`
    })

    this.call('openNotification', {
      title: this.call('polyglot', 'editor.db.restoredTypes.title'),
      message: this.call('polyglot', 'editor.db.restoredTypes.message').replace(
        '{count}',
        String(update.length)
      ),
      type: 'success'
    })
  }
}

export async function restoreMissingComponents() {
  const data = this.getData()
  const schema = this.getSchema()
  const update = []

  ;['pages', 'components'].forEach((type) => {
    Object.keys(data[type]).forEach((itemKey) => {
      const schemaItem = schema[type][itemKey]
      const dataItem = data[type][itemKey]
      if (dataItem && !schemaItem) {
        update.push([
          'update',
          ['schema', type, itemKey],
          {
            title: itemKey,
            key: itemKey,
            type: type,
            settings: {
              gridOptions: {}
            },
            interactivity: [],
            dataTypes: []
          }
        ])
      }
    })
  })

  if (update.length) {
    this.sdk.updateData(update, {
      message: `Restored ${update.length} components`
    })

    this.call('openNotification', {
      title: this.call('polyglot', 'editor.db.restoredComponents.title'),
      message: this.call('polyglot', 'editor.db.restoredComponents.message').replace(
        '{count}',
        String(update.length)
      ),
      type: 'success'
    })
  }
}

export async function clearCache(opts = {}) {
  const schema = this.getSchema()
  const update = []
  Object.keys(schema).forEach((type) => {
    Object.keys(schema[type]).forEach((itemKey) => {
      const val = schema[type][itemKey]

      // cleanup all values without key
      if (!val.key && type !== 'designSystem') {
        update.push(['delete', [type, itemKey]])
        update.push(['delete', ['schema', type, itemKey]])
      }
      if (['pages', 'components', 'functions', 'methods', 'snippets'].includes(type)) {
        update.push(['delete', ['schema', type, itemKey, 'code']])
        update.push(['delete', ['schema', type, itemKey, 'codeStr']])
      }
    })
  })

  if (update.length) {
    this.sdk.updateData(update, {
      message: `Cleared ${update.length} components`
    })

    if (opts.skipNotification) return

    this.call('openNotification', {
      title: this.call('polyglot', 'editor.db.cacheCleared.title'),
      message: this.call('polyglot', 'editor.db.cacheCleared.message').replace(
        '{count}',
        String(update.length)
      ),
      type: 'success'
    })
  }
}

export function getDefaultValuePerType(type, key) {
  if (this.call('hasType', 'ARTBOARD_TYPES', type)) {
    return {
      key,
      type,
      value: {},
      settings: {}
    }
  }

  if (this.call('hasType', 'FUNCTION_TYPES', type)) {
    // No code generation from the KEY (ws crm review, N5 batch): this used
    // `new Function(\`return function ${key}() {…}\`)()`, so a co-editor's
    // crafted key (`x(){}(<code>), function y`) ran on the shell origin when
    // the victim clicked Duplicate. The default is a plain function named by
    // defineProperty; the code template names it only with a plain identifier.
    const fnName = /^[A-Za-z_$][\w$]*$/.test(String(key)) ? String(key) : 'fn'
    const value = function () {
      /* console.log(this, this.state, this.context) */
    }
    Object.defineProperty(value, 'name', { value: fnName, configurable: true })
    return {
      key,

      value,
      code: `export default function ${fnName} () {
        // console.log(this, this.state, this.context)
      }`,
      type
    }
  }

  return {
    key,
    type,
    value: {}
  }
}

export function buildSchemaItemFromData(type, key, value) {
  const schemaType = this.getPlural ? this.getPlural(type) : type

  const base = {
    title: key,
    key,
    type: schemaType
  }

  if (['pages', 'components'].includes(schemaType)) {
    base.settings = {
      gridOptions: {}
    }
    base.props = {}
    base.interactivity = []
    base.dataTypes = []
    base.error = null
  }

  let code = null
  try {
    const rawCode = this.call ? this.call('stringifyCode', value) : null
    if (rawCode) {
      code = this.call('encodeNewlines', rawCode)
    }
  } catch (e) {
    console.error('Failed to build schema code from data', e)
  }

  if (code) {
    base.code = code
  }

  return base
}
